import { MutableRefObject } from 'react';

type AnyRef = MutableRefObject<any> | any;

const resolveInputElFrom = (e: any, ref: AnyRef): HTMLInputElement | null => {
  const t: any = e?.target;
  if (t && typeof t.value === 'string' && typeof t.selectionStart === 'number') {
    return t as HTMLInputElement;
  }
  const refCurrent: any = (ref as any)?.current;
  if (refCurrent) {
    if (refCurrent.input && typeof refCurrent.input.value === 'string') return refCurrent.input as HTMLInputElement;
    if (refCurrent.inputElement && typeof refCurrent.inputElement.value === 'string') return refCurrent.inputElement as HTMLInputElement;
    if (refCurrent.nativeElement && typeof refCurrent.nativeElement.value === 'string') return refCurrent.nativeElement as HTMLInputElement;
    if (refCurrent.resizableTextArea?.textArea && typeof refCurrent.resizableTextArea.textArea.value === 'string') return refCurrent.resizableTextArea.textArea as HTMLInputElement;
  }
  const ct = e?.currentTarget as HTMLElement;
  const qs = ct?.querySelector?.('input');
  return (qs as HTMLInputElement) || null;
};

export const createNumberOnKeyDown = ({
  ref,
  value,
  onChange,
  maxLength,
  max,
  deletefull,
}: {
  ref: AnyRef;
  value: any;
  onChange?: any;
  maxLength?: number;
  max?: number;
  deletefull?: boolean;
}) => (e: any) => {
  if (['Backspace', 'Delete'].includes(e.key) && deletefull) {
    e.preventDefault();
    onChange?.(null, ref);
    return;
  }

  if (['ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Backspace', 'Delete'].includes(e.key)) {
    return;
  }

  if (!/^[0-9]$/.test(e.key)) {
    e.preventDefault();
    return;
  }

  const inputEl = resolveInputElFrom(e, ref);
  const domValue = (inputEl && typeof inputEl.value === 'string') ? inputEl.value : String(value || '');
  const selectionStart = (inputEl as any)?.selectionStart ?? domValue.length;
  const selectionEnd = (inputEl as any)?.selectionEnd ?? domValue.length;
  const before = domValue.slice(0, selectionStart);
  const after = domValue.slice(selectionEnd);

  const digitsOnly = domValue.replace(/\D/g, '');
  const digitsBefore = before.replace(/\D/g, '');
  const digitsAfter = after.replace(/\D/g, '');
  const digitIdxStart = digitsBefore.length;
  const digitIdxEnd = digitsOnly.length - digitsAfter.length;
  const proposedDigitsBySelection = (
    digitsOnly.slice(0, digitIdxStart) + e.key + digitsOnly.slice(digitIdxEnd)
  );

  if (maxLength) {
    const hasSelection = selectionStart !== selectionEnd;
    const replacesAllDigits = (digitIdxStart === 0 && digitIdxEnd === digitsOnly.length);
    const fullReplace = hasSelection && replacesAllDigits;
    const selectedTextFallback = (typeof window !== 'undefined' && window.getSelection) ? window.getSelection()?.toString() || '' : '';
    const selectedDigitsFallback = selectedTextFallback.replace(/\D/g, '');
    let candidateDigits = fullReplace ? e.key : proposedDigitsBySelection;
    if (!hasSelection && selectedDigitsFallback) {
      const idx = digitsOnly.indexOf(selectedDigitsFallback);
      if (idx !== -1) {
        candidateDigits = digitsOnly.slice(0, idx) + e.key + digitsOnly.slice(idx + selectedDigitsFallback.length);
      }
    }
    if (candidateDigits.length > maxLength) {
      e.preventDefault();
      return;
    }
  }

  const hasSelection = selectionStart !== selectionEnd;
  const replacesAllDigits = (digitIdxStart === 0 && digitIdxEnd === digitsOnly.length);
  const fullReplace = hasSelection && replacesAllDigits;
  const selectedTextFallback = (typeof window !== 'undefined' && window.getSelection) ? window.getSelection()?.toString() || '' : '';
  const selectedDigitsFallback = selectedTextFallback.replace(/\D/g, '');
  let proposedDigits = fullReplace ? e.key : proposedDigitsBySelection;
  if (!hasSelection && selectedDigitsFallback) {
    const idx = digitsOnly.indexOf(selectedDigitsFallback);
    if (idx !== -1) {
      proposedDigits = digitsOnly.slice(0, idx) + e.key + digitsOnly.slice(idx + selectedDigitsFallback.length);
    }
  }
  const numericValue = proposedDigits === '' ? NaN : parseInt(proposedDigits, 10);

  if (max !== undefined && !isNaN(numericValue) && numericValue > max) {
    e.preventDefault();
    return;
  }
};

export const createNumberOnBeforeInput = ({
  ref,
  value,
  maxLength,
  max,
}: {
  ref: AnyRef;
  value: any;
  maxLength?: number;
  max?: number;
}) => (e: any) => {
  if (!e || e.inputType !== 'insertText') return;
  const data: string = e.data ?? '';
  if (!/^\d+$/.test(data)) {
    e.preventDefault();
    return;
  }

  const inputEl = resolveInputElFrom(e, ref);
  const domValue = (inputEl && typeof inputEl.value === 'string') ? inputEl.value : String(value || '');
  const selectionStart = (inputEl as any)?.selectionStart ?? domValue.length;
  const selectionEnd = (inputEl as any)?.selectionEnd ?? domValue.length;
  const before = domValue.slice(0, selectionStart);
  const after = domValue.slice(selectionEnd);

  const digitsOnly = domValue.replace(/\D/g, '');
  const digitsBefore = before.replace(/\D/g, '');
  const digitsAfter = after.replace(/\D/g, '');
  const digitIdxStart = digitsBefore.length;
  const digitIdxEnd = digitsOnly.length - digitsAfter.length;
  const selectedText = (typeof window !== 'undefined' && window.getSelection) ? window.getSelection()?.toString() || '' : '';
  const selectedDigits = selectedText.replace(/\D/g, '');
  const fullReplace = selectedDigits && selectedDigits === digitsOnly;
  const proposedDigits = fullReplace ? data : (digitsOnly.slice(0, digitIdxStart) + data + digitsOnly.slice(digitIdxEnd));

  if (maxLength && proposedDigits.length > maxLength) {
    e.preventDefault();
    return;
  }
  const numericValue = proposedDigits === '' ? NaN : parseInt(proposedDigits, 10);
  if (max !== undefined && !isNaN(numericValue) && numericValue > max) {
    e.preventDefault();
    return;
  }
};

export const createGenericOnKeyDown = ({
  type,
  value,
  onChange,
  maxLength,
  max,
  min,
}: {
  type?: string;
  value: any;
  onChange?: any;
  maxLength?: number;
  max?: number;
  min?: number;
}) => (e: any) => {
  if (['Backspace', 'Delete'].includes(e.key) && type === 'number') {
    e.preventDefault();
    onChange?.({ target: { value: '' } });
    return;
  }

  if (['ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
    return;
  }

  if (maxLength) {
    const currentValue = String(value || '').replace(/\D/g, '');
    if (currentValue.length >= maxLength) {
      e.preventDefault();
      return;
    }
  }

  if (type === 'number' && max !== undefined) {
    const currentValue = String(value || '').replace(/\D/g, '');
    const newValue = currentValue + e.key;
    const numericValue = parseInt(newValue, 10);
    if (!isNaN(numericValue) && numericValue > max) {
      e.preventDefault();
      return;
    }
  }

  if (type === 'number' && min !== undefined && value !== null && value !== undefined && value !== '') {
    const currentValue = String(value || '').replace(/\D/g, '');
    const newValue = currentValue + e.key;
    const numericValue = parseInt(newValue, 10);
    if (!isNaN(numericValue) && numericValue < min) {
      e.preventDefault();
      return;
    }
  }
};


